import { useState } from 'react'
import { MapPin, RefreshCw } from 'lucide-react'
import { useSearchParams } from 'react-router-dom'
import { KpiStrip, MetricCard, Panel, SectionHeader } from '@/components/erp/primitives'
import {
  AsyncMultiSelect, DateRange, FilterBar, MultiSelect,
} from '@/shared/ui/filters'
import { CursorPager } from '@/shared/ui/CursorPager'
import { EmptyState, ErrorStrip, Forbidden, LoadingBlock } from '@/shared/ui/states'
import { RankedBars, Sparkbars } from '@/shared/ui/charts'
import { useCursorPage } from '@/shared/hooks/useCursorPage'
import { useCan } from '@/shared/auth/session'
import { listUnits } from '@/features/geography/api'
import { dayKey, fmtNumber, fmtRelative } from '@/shared/lib/format'
import type { ActivityResponse } from '@/shared/api/types'
import { activityFeed, listMobilizers } from './api'
import {
  ACTIVITY, ACTIVITY_ORDER, ActivityChip, SourceChip, activityLabel, roleLabel,
} from './vocab'
import { MobilizerDrawer } from './MobilizerDrawer'

/**
 * The field activity feed.
 *
 * ## Keyset paging, and why the cursor looks odd
 *
 * New rows arrive at the front constantly. Offset page 2 of a list that grew by
 * forty rows shows you page 1 again — so this is keyset-paged, and the cursor is
 * composite rather than the bare uuid the rest of the app uses. `occurredAt` is
 * supplied by the device and can be backdated up to a week, so id order is not
 * time order and a uuid cursor would skip rows. It is opaque; nothing here
 * parses it.
 *
 * ## Dates, not instants
 *
 * `from`/`to` are `yyyy-MM-dd` and the server buckets them in the deployment's
 * timezone. An activity at 01:00 in Nairobi belongs to that day; a browser on a
 * laptop set to UTC that sent an instant would file it under the day before.
 *
 * ## No auto-refresh
 *
 * PollSphere had a five-second poll — implemented inside a `useMemo`, so its
 * cleanup never ran and every render started another interval that nothing
 * cleared. Rather than reimplement a timer nobody asked for, this has a Refresh
 * button. If live tailing is wanted later it belongs on a socket, not a poll
 * that quietly multiplies itself.
 */

const PAGE = 50

export function MobilizerActivityPage() {
  const canRead = useCan('mobilizer:read')
  const [params, setParams] = useSearchParams()

  const from = params.get('from') ?? ''
  const to = params.get('to') ?? ''
  const source = params.get('source') ?? ''
  const [types, setTypes] = useState<string[]>([])
  const [people, setPeople] = useState<{ value: string; label: string }[]>([])
  const [unit, setUnit] = useState<{ value: string; label: string }[]>([])
  const [openId, setOpenId] = useState<string | null>(null)

  const patch = (next: Record<string, string | null>) => {
    const sp = new URLSearchParams(params)
    for (const [k, v] of Object.entries(next)) {
      if (v === null || v === '') sp.delete(k); else sp.set(k, v)
    }
    setParams(sp, { replace: true })
  }

  const mobilizerIds = people.map((p) => p.value)
  const unitId = unit[0]?.value

  const page = useCursorPage<ActivityResponse>(
    (cursor) => (canRead
      ? activityFeed({
          from: from || undefined,
          to: to || undefined,
          source: source || undefined,
          activityType: types.length ? types : undefined,
          mobilizerId: mobilizerIds.length ? mobilizerIds : undefined,
          unitId,
        }, cursor, PAGE)
      : Promise.resolve({ content: [], hasMore: false })),
    [canRead, from, to, source, types.join(','), mobilizerIds.join(','), unitId])

  if (!canRead) return <Forbidden what="the activity feed" />

  const rows = page.rows
  const byType = new Map<string, number>()
  for (const r of rows) byType.set(r.activityTypeCode, (byType.get(r.activityTypeCode) ?? 0) + 1)
  const byDay = new Map<string, number>()
  for (const r of rows) {
    // `occurredAt.slice(0, 10)` is the tempting version and it is wrong by a day
    // for everything between midnight and 03:00 — the wire is UTC and Nairobi is
    // UTC+3. This is the same off-by-one the header warns about; it would be
    // embarrassing to commit it in the chart underneath.
    const day = dayKey(r.occurredAt)
    if (!day) continue
    byDay.set(day, (byDay.get(day) ?? 0) + 1)
  }
  const days = [...byDay.entries()].sort(([a], [b]) => (a < b ? -1 : 1))
  const people24 = new Set(rows
    .filter((r) => Date.parse(r.occurredAt) > Date.now() - 86_400_000)
    .map((r) => r.mobilizerId))
  const activeCount = [from, to, source, unitId].filter(Boolean).length
    + (types.length ? 1 : 0) + (mobilizerIds.length ? 1 : 0)

  return (
    <div className="space-y-4">
      <SectionHeader
        title="Field activity"
        description="Append-only. Nothing here can be edited or deleted — an activity that can be changed after the fact is not evidence."
        right={(
          <button type="button" className="erp-btn" aria-label="Refresh"
                  onClick={() => void page.reload()}>
            <RefreshCw className="h-3.5 w-3.5" /> <span className="axis-hide-sm">Refresh</span>
          </button>
        )}
      />

      <KpiStrip>
        <MetricCard label="On this page" value={fmtNumber(rows.length)}
                    sub="the counts below describe these rows, not the whole log" accent />
        <MetricCard label="In the last day" value={fmtNumber(rows.filter(
          (r) => Date.parse(r.occurredAt) > Date.now() - 86_400_000).length)}
                    sub="of the rows shown" />
        <MetricCard label="People active" value={fmtNumber(people24.size)}
                    sub="distinct mobilizers in the last day" />
        <MetricCard label="Credited automatically" value={fmtNumber(rows.filter(
          (r) => r.sourceCode === 'SYSTEM').length)}
                    sub="raised elsewhere, credited back" />
      </KpiStrip>

      <FilterBar activeCount={activeCount}>
        <DateRange from={from} to={to}
                   onChange={(next) => patch({ from: next.from, to: next.to })} />
        <MultiSelect
          label="Activity"
          options={ACTIVITY_ORDER.map((c) => ({ value: c, label: ACTIVITY[c].label }))}
          selected={types}
          onChange={setTypes}
        />
        <select className="erp-input" value={source} aria-label="Source"
                onChange={(e) => patch({ source: e.target.value })}>
          <option value="">Field and automatic</option>
          <option value="FIELD">Captured in the field</option>
          <option value="SYSTEM">Credited automatically</option>
          <option value="IMPORT">Backfilled</option>
        </select>
        <AsyncMultiSelect
          label="Mobilizer"
          selected={people}
          onChange={setPeople}
          placeholder="Search by name"
          search={async (term) => {
            const found = await listMobilizers({ q: term }, undefined, 25)
            return found.content.map((m) => ({
              value: m.id, label: m.fullName,
              sub: `${roleLabel(m.roleCode)} · ${m.primaryUnitName ?? 'no unit'}`,
            }))
          }}
        />
        <AsyncMultiSelect
          label="Unit"
          selected={unit}
          onChange={(next) => setUnit(next.slice(-1))}
          placeholder="Search a county, ward or station"
          search={async (term) => {
            const found = await listUnits({ q: term } as never, 0, 10)
            return found.content.map((u) => ({ value: u.id, label: u.name, sub: u.code }))
          }}
        />
      </FilterBar>

      <ErrorStrip message={page.error} />

      <div className="grid gap-4 lg:grid-cols-[1fr_20rem]">
        <Panel bodyClassName="p-0">
          <div className="p-4">
            {page.loading && rows.length === 0 ? (
              <LoadingBlock label="Loading the feed…" />
            ) : rows.length === 0 ? (
              <EmptyState
                title="Nothing in this window"
                hint="Widen the dates, or clear a filter. An empty feed with filters applied is not the same as an empty log."
              />
            ) : (
              <ul className="divide-y divide-border">
                {rows.map((a) => (
                  <li key={a.id} className="py-2.5 flex items-start gap-3" data-activity={a.id}>
                    <span className="min-w-0 flex-1">
                      <span className="flex flex-wrap items-center gap-1.5">
                        <ActivityChip code={a.activityTypeCode} />
                        <SourceChip code={a.sourceCode} />
                        <button
                          type="button"
                          className="text-erp-sm font-medium hover:text-chocolate truncate"
                          onClick={() => setOpenId(a.mobilizerId)}
                        >
                          {a.mobilizerName ?? 'Unknown mobilizer'}
                        </button>
                      </span>
                      <span className="block text-erp-xs text-muted-foreground mt-0.5">
                        {a.unitName ?? 'no unit'}
                        {/* Masked, always. This is the most-viewed screen in the
                            feature and exactly where an unmasked column would go
                            unnoticed for a year. */}
                        {a.mobilizerMaskedPhone ? ` · ${a.mobilizerMaskedPhone}` : ''}
                        {a.latitude !== null && a.latitude !== undefined && (
                          <span className="inline-flex items-center gap-1 ml-1">
                            <MapPin className="h-3 w-3" />
                            ±{a.accuracyMetres ?? '?'} m
                          </span>
                        )}
                      </span>
                    </span>
                    <span className="text-erp-xs text-muted-foreground whitespace-nowrap"
                          title={a.occurredAt}>
                      {fmtRelative(a.occurredAt)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <CursorPager
            page={page.page}
            count={rows.length}
            hasMore={page.hasMore}
            canGoBack={page.canGoBack}
            onBack={page.back}
            onNext={page.next}
            unit="activities"
          />
        </Panel>

        <div className="space-y-4">
          <Panel title="By day" description="The rows on this page, bucketed in the deployment's timezone.">
            <Sparkbars
              points={days.map(([day, count]) => ({ label: day.slice(5), value: count }))}
              emptyMessage="Nothing to plot yet."
            />
          </Panel>
          <Panel title="What kind" description="On this page. Change the filters to change the question.">
            <RankedBars
              points={[...byType.entries()]
                .sort((a, b) => b[1] - a[1])
                .map(([code, count]) => ({ label: activityLabel(code), value: count }))}
              emptyMessage="Nothing to count yet."
            />
          </Panel>
        </div>
      </div>

      {/* Keyed on the subject — see the note in RosterPage. */}
      <MobilizerDrawer
        key={openId ?? 'closed'}
        id={openId}
        onClose={() => setOpenId(null)}
        onChanged={() => void page.reload()}
        onOpenOther={(id) => setOpenId(id)}
      />
    </div>
  )
}
